/*
 * The To-do view's rules: what it lists and in which order. One set of rules for both places that show it, the
 * Android To-do widget (apps/android/.../TodoRules.kt, TodoRulesTest.kt) and the To-do panel on Home in the web and
 * Android apps (this file, test/todo.test.ts). Change both together, tests included.
 */

/** One open task as the To-do view shows it. */
export interface TodoTask {
  id: string;
  title: string;
  /** Epoch ms; an all-day task's due is its day's local midnight. */
  due: number | null;
  allDay: boolean;
  flagged: boolean;
  /** Added from this To-do view a moment ago: listed first, so a new one shows at once. */
  justAdded?: boolean;
}

export type TodoFilter = "all" | "today" | "flagged";
export const TODO_FILTERS: TodoFilter[] = ["all", "today", "flagged"];
export const TODO_LABELS: Record<TodoFilter, string> = { all: "All", today: "Today", flagged: "Flagged" };

/** The filter chip's next value: All → Today → Flagged → All. */
export function nextTodoFilter(filter: TodoFilter): TodoFilter {
  return TODO_FILTERS[(TODO_FILTERS.indexOf(filter) + 1) % TODO_FILTERS.length]!;
}

/** Due by the end of today, so overdue ones count too. */
export function dueToday(t: TodoTask, endOfToday: number): boolean {
  return t.due != null && t.due <= endOfToday;
}

/** Past its time; an all-day task only once its day is over. */
export function overdue(t: TodoTask, now: number, startOfToday: number): boolean {
  return t.due != null && (t.allDay ? t.due < startOfToday : t.due < now);
}

/**
 * Just added here first, then today's, then overdue (latest first: a reminder left from years ago shouldn't push
 * today's off), then flagged, then the rest; within each by due date, then in the given order.
 */
export function orderTodo<T extends TodoTask>(tasks: T[], filter: TodoFilter, startOfToday: number, endOfToday: number): T[] {
  const shown = filter === "today" ? tasks.filter((t) => dueToday(t, endOfToday)) : filter === "flagged" ? tasks.filter((t) => t.flagged) : tasks;
  const group = (t: T) =>
    t.justAdded ? 0 : t.due != null && t.due >= startOfToday && t.due <= endOfToday ? 1 : t.due != null && t.due < startOfToday ? 2 : t.flagged ? 3 : 4;
  const dueKey = (t: T, g: number) => (g === 2 ? -t.due! : (t.due ?? Number.MAX_SAFE_INTEGER));
  return shown
    .map((t, i) => ({ t, i, g: group(t) }))
    .sort((a, b) => a.g - b.g || dueKey(a.t, a.g) - dueKey(b.t, b.g) || a.i - b.i)
    .map((x) => x.t);
}

/** Local midnight today and the last millisecond of today. */
export function todayBounds(now = Date.now()): { start: number; end: number } {
  const d = new Date(now);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() - 1;
  return { start, end };
}
